import {
  ActionType,
  EntityType,
  ModuleType,
} from "@shared/api/queries/queryKeyFactory";

export const bulkBillingKeyFactory = {
  // ============================================================================
  // STEP 1: Bill Cycle Keys
  // ============================================================================
  billingCyclesList: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, string, ActionType, number] => [
    "bx",
    "bulkBilling",
    "billingCyclesList",
    "list",
    remoteUtilityId,
  ],

  billingCycleDetails: (
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "billingCycleDetails",
    "details",
    cycleId,
  ],

  billTemplatesList: (
    remoteUtilityId: number,
  ): [ModuleType, EntityType, string, ActionType, number] => [
    "bx",
    "bulkBilling",
    "billTemplatesList",
    "list",
    remoteUtilityId,
  ],

  draftDetails: (
    draftId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "draftDetails",
    "details",
    draftId,
  ],

  // ============================================================================
  // STEP 2: Fetch Consumer Data Keys
  // ============================================================================
  dashboardSummary: (
    remoteUtilityId: number,
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "dashboardSummary",
    "summary",
    remoteUtilityId,
    cycleId,
  ],

  consumerAnalysis: (
    remoteUtilityId: number,
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "consumerAnalysis",
    "details",
    remoteUtilityId,
    cycleId,
  ],

  consumersList: (
    remoteUtilityId: number,
    cycleId: string | number,
    page: number,
    limit: number,
  ): [
    ModuleType,
    EntityType,
    string,
    ActionType,
    number,
    string | number,
    number,
    number,
  ] => [
    "bx",
    "bulkBilling",
    "consumersList",
    "list",
    remoteUtilityId,
    cycleId,
    page,
    limit,
  ],

  billingPlans: (
    remoteUtilityId: number,
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "billingPlans",
    "list",
    remoteUtilityId,
    cycleId,
  ],

  planDetails: (
    remoteUtilityId: number,
    planId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "planDetails",
    "details",
    remoteUtilityId,
    planId,
  ],

  outstandingAccounts: (
    remoteUtilityId: number,
    cycleId: string | number,
    page: number,
    limit: number,
    sortBy: string,
    sortOrder: string,
  ): [
    ModuleType,
    EntityType,
    string,
    ActionType,
    number,
    string | number,
    number,
    number,
    string,
    string,
  ] => [
    "bx",
    "bulkBilling",
    "outstandingAccounts",
    "list",
    remoteUtilityId,
    cycleId,
    page,
    limit,
    sortBy,
    sortOrder,
  ],

  paymentDetails: (
    remoteUtilityId: number,
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "paymentDetails",
    "details",
    remoteUtilityId,
    cycleId,
  ],

  // ============================================================================
  // STEP 3: Meter Reading Keys
  // ============================================================================
  meterReadingSummary: (
    remoteUtilityId: number,
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, number, string | number] => [
    "bx",
    "bulkBilling",
    "meterReadingSummary",
    "summary",
    remoteUtilityId,
    cycleId,
  ],

  meterReadingAnalysis: (
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "meterReadingAnalysis",
    "details",
    cycleId,
  ],

  meterReadings: (
    remoteUtilityId: number,
    cycleId: string | number,
    draftId: string | number,

    filters?: {
      readingType?: string;
      search?: string;
      meterType?: string;
      service?: string;
      veeStatus?: string;
      status?: string;
      consumptionFlag?: string;
    },
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    number,
    string | number,
    string | number,
    any?,
  ] => [
    "bx",
    "bulkBilling",
    "meterReadings",
    "list",
    remoteUtilityId,
    cycleId,
    draftId,
    filters,
  ],

  meterReplacementCases: (
    cycleId: string | number,
    page: number,
    limit: number,
    search?: string,
  ): [
    ModuleType,
    EntityType,
    string,
    ActionType,
    string | number,
    number,
    number,
    string?,
  ] => [
    "bx",
    "bulkBilling",
    "meterReplacementCases",
    "list",
    cycleId,
    page,
    limit,
    search,
  ],

  trueUpSummary: (
    cycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "trueUpSummary",
    "summary",
    cycleId,
  ],

  trueUpCases: (
    cycleId: string | number,
    page: number,
    limit: number,
    search?: string,
    status?: string,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
    string?,
    string?,
  ] => [
    "bx",
    "bulkBilling",
    "list",
    "trueUpCases",
    cycleId,
    page,
    limit,
    search,
    status,
  ],

  billDaysThresholdSummary: (
    cycleId: string,
  ): [ModuleType, EntityType, ActionType, string, string] => [
    "bx",
    "bulkBilling",
    "summary",
    "billDaysThresholdSummary",
    cycleId,
  ],

  billDaysThresholdList: (
    cycleId: string | number,
    page: number,
    limit: number,
    threshold?: string,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
    string?,
  ] => [
    "bx",
    "bulkBilling",
    "list",
    "billDaysThresholdList",
    cycleId,
    page,
    limit,
    threshold,
  ],

  // ============================================================================
  // STEP 4: Bill Generation Keys
  // ============================================================================
  trackBillGenerationProgress: (
    taskId: string,
  ): [ModuleType, EntityType, string, string, string] => [
    "bx",
    "bulkBilling",
    "progress",
    "trackBillGenerationProgress",
    taskId,
  ],

  billCycleSummary: (
    billCycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "billCycleSummary",
    "summary",
    billCycleId,
  ],

  failedBillsList: (
    runId: string | number,
    page: number,
    limit: number,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
  ] => ["bx", "bulkBilling", "list", "failedBillsList", runId, page, limit],

  // ============================================================================
  // STEP 5: Review Bills Keys
  // ============================================================================
  billingSummary: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "billingSummary",
    "summary",
    runId,
  ],

  billsList: (
    runId: string | number,
    page: number,
    limit: number,
    search?: string,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
    string?,
  ] => ["bx", "bulkBilling", "list", "billsList", runId, page, limit, search],

  excludedConsumers: (
    runId: string | number,
    page: number,
    limit: number,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
  ] => ["bx", "bulkBilling", "list", "excludedConsumers", runId, page, limit],

  distributionOverview: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "distributionOverview",
    "summary",
    runId,
  ],

  billRunSummary: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "billRunSummary",
    "summary",
    runId,
  ],

  billRunBillsList: (
    runId: string | number,
    page: number,
    limit: number,
    search?: string,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
    string?,
  ] => [
    "bx",
    "bulkBilling",
    "list",
    "billRunBillsList",
    runId,
    page,
    limit,
    search,
  ],

  excludedConsumersSummary: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "excludedConsumersSummary",
    "summary",
    runId,
  ],

  excludedConsumersList: (
    runId: string | number,
    page: number,
    limit: number,
    search?: string,
  ): [
    ModuleType,
    EntityType,
    ActionType,
    string,
    string | number,
    number,
    number,
    string?,
  ] => [
    "bx",
    "bulkBilling",
    "list",
    "excludedConsumersList",
    runId,
    page,
    limit,
    search,
  ],

  activityLog: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "activityLog",
    "list",
    runId,
  ],

  serviceCharges: (
    billCycleId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "serviceCharges",
    "list",
    billCycleId,
  ],

  billRuns: (
    filters: Record<string, any>,
  ): [ModuleType, EntityType, string, ActionType, any?] => [
    "bx",
    "bulkBilling",
    "billRuns",
    "list",
    filters,
  ],

  runDetails: (
    runId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "runDetails",
    "details",
    runId,
  ],

  getExportBillData: (
    batchId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "exportBillData",
    "details",
    batchId,
  ],

  getExportMeterReading: (
    batchId: string | number,
  ): [ModuleType, EntityType, string, ActionType, string | number] => [
    "bx",
    "bulkBilling",
    "exportMeterReading",
    "details",
    batchId,
  ],
};
